import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row, Button, Divider, message, Checkbox, Modal, Input } from 'antd';
import { useQuery, useMutation } from '@apollo/client';
import InputFilterItem from '../../../../delivery/components/InputFilterItem';
import PricingItemPanel from '../PriceItemPanel';
import DspDeliveryFeeTabb, { DspDeliveryFeeData } from './DspDeliveryFeeTabb';
import { NEW_PRICING_SCHEME, type PricingSchemeInputData, type NewPricingSchemeResponse } from 'graphql/mutations/settlement/newPricingScheme';
import { UPDATE_PRICING_SCHEME, type UpdatePricingSchemeResponse } from 'graphql/mutations/settlement/updatePricingScheme';
import { GET_PRICING_FEE_ITEMS_BY_SCENARIO } from 'graphql/queries/settlement/getSettlementCommonQueries';
import { GET_PRICING_SCHEME_DETAIL, type PricingSchemeDetailResponse } from 'graphql/queries/settlement/getPricingSchemeDetail';
import { GET_WAREHOUSES_FOR_CENTRAL, WarehousesResponse } from '../../../exceptions/graphql/queries/getWarehouseForCentral';
import {ResourceCode} from "../../../../../constants/resourceCodes";
import {HasResource} from "../../../../../components/HasResource";
import { resolvePricingSchemeMessage } from 'utils/graphqlUtils';

const { TextArea } = Input;

// DSP 常量参数
const DSP_CLIENT_ID = 0;
const DSP_PRICING_FEE_ITEM_CODE = 1001002001;

/** Line/线路 仅允许：英文字母、数字、下划线、连字符、英文逗号、空格 */
const LINE_INPUT_REGEX = /^[a-zA-Z0-9_\-\s,]*$/;
const isLineInputValid = (value: string): boolean => LINE_INPUT_REGEX.test(value);

interface DSPCreateSchemeCompProps {
    businessScenario: number | null;
    viewMode: 'create' | 'view' | 'edit';
    pricingSchemeId?: number | null;
    /** 复制时预填的方案 ID，仅 viewMode='create' 时使用 */
    copyFromSchemeId?: number | null;
    onSaveSuccess?: () => void;
    onCancel?: () => void;
}

const DSPCreateSchemeComp: React.FC<DSPCreateSchemeCompProps> = ({
    businessScenario,
    viewMode,
    pricingSchemeId,
    copyFromSchemeId = null,
    onSaveSuccess,
    onCancel
}) => {
    const { t } = useTranslation();
    const isViewMode = viewMode === 'view';
    const isEditMode = viewMode === 'edit';

    // 基础信息
    const [pricingSchemeName, setPricingSchemeName] = useState<string>('');
    const [pricingSchemeDescription, setPricingSchemeDescription] = useState<string>('');

    // 获取费用项列表（根据业务场景）
    const { data: feeItemData } = useQuery(GET_PRICING_FEE_ITEMS_BY_SCENARIO, {
        variables: { businessScenarioCode: businessScenario || 0 },
        skip: !businessScenario,
    });

    // 获取定价方案详情（查看/编辑用 pricingSchemeId，复制用 copyFromSchemeId）
    const detailSchemeId = (viewMode === 'view' || viewMode === 'edit') ? pricingSchemeId : copyFromSchemeId;
    const { data: schemeDetailData, loading: schemeDetailLoading } = useQuery<PricingSchemeDetailResponse>(
        GET_PRICING_SCHEME_DETAIL,
        {
            variables: { pricingSchemeId: detailSchemeId! },
            skip: !detailSchemeId,
            fetchPolicy: 'network-only'
        }
    );

    // 获取 warehouse 列表，用于加载时匹配 warehouse
    const { data: warehouseData } = useQuery<WarehousesResponse>(GET_WAREHOUSES_FOR_CENTRAL, {
        fetchPolicy: 'cache-first',
    });

    // 费用项列表
    const feeItems = useMemo(() => {
        if (!feeItemData?.getPricingFeeItemsByBusinessScenarioCode) return [];
        return feeItemData.getPricingFeeItemsByBusinessScenarioCode;
    }, [feeItemData]);

    // 选中的费用项集合
    const [selectedFeeItemCodes, setSelectedFeeItemCodes] = useState<Set<number>>(new Set());

    // 费用项数据状态管理
    const [feeItemDataMap, setFeeItemDataMap] = useState<Map<number, DspDeliveryFeeData>>(new Map());

    // Mutations
    const [submitNewPricingScheme, { loading: savingNew }] = useMutation<NewPricingSchemeResponse, { input: PricingSchemeInputData }>(
        NEW_PRICING_SCHEME,
        { fetchPolicy: 'network-only', errorPolicy: 'all' }
    );

    const [updatePricingScheme, { loading: savingUpdate }] = useMutation<UpdatePricingSchemeResponse, { input: PricingSchemeInputData }>(
        UPDATE_PRICING_SCHEME,
        { fetchPolicy: 'network-only', errorPolicy: 'all' }
    );

    const saving = savingNew || savingUpdate;

    // 加载定价方案详情数据（查看/编辑模式，或复制模式预填）
    useEffect(() => {
        const shouldFillFromDetail = (viewMode === 'view' || viewMode === 'edit') || (viewMode === 'create' && copyFromSchemeId);
        const detailResult = schemeDetailData?.getPricingSchemeDetail;
        if (detailResult?.messageKey || detailResult?.message) {
            message.error(resolvePricingSchemeMessage(detailResult, '加载定价方案详情失败'));
        }
        if (shouldFillFromDetail && schemeDetailData?.getPricingSchemeDetail && feeItems.length > 0) {
            const detail = schemeDetailData.getPricingSchemeDetail;

            // 设置基础信息
            setPricingSchemeName(detail.pricingSchemeName);
            setPricingSchemeDescription(detail.pricingSchemeDescription || '');

            // 为每个费用项还原数据
            const newFeeItemDataMap = new Map<number, DspDeliveryFeeData>();
            const selectedFeeItemCodesSet = new Set<number>();

            detail.priceConfig.forEach((priceConfig: any) => {
                const feeItemCode = priceConfig.pricingFeeItemCode;
                const feeItemCodeNum = typeof feeItemCode === 'string' ? parseInt(feeItemCode, 10) : feeItemCode;

                // 解析 DSP 配送费数据 - contentPriceConfig 是数组结构
                const cards = priceConfig.allPriceConfig.map((allPriceConfig: any) => {
                    try {
                        if (allPriceConfig.contentPriceConfig) {
                            const contentConfigArray = JSON.parse(allPriceConfig.contentPriceConfig);
                            // contentPriceConfig 是数组，取第一个元素
                            const contentConfig = Array.isArray(contentConfigArray) ? contentConfigArray[0] : contentConfigArray;
                            if (contentConfig) {
                                // 从保存的数据中恢复，根据 warehouseSortingCode 和 warehouseName 匹配对应的 warehouse
                                const savedWarehouseName = contentConfig.warehouseName || '';
                                const savedWarehouseSortingCode = contentConfig.warehouseSortingCode || '';
                                
                                // 在 warehouse 列表中查找匹配的 warehouse
                                let matchedWarehouse: any = null;
                                if (warehouseData?.warehouses && savedWarehouseName && savedWarehouseSortingCode) {
                                    matchedWarehouse = warehouseData.warehouses.find((w: any) => {
                                        if (w.siteType !== 2) return false; // 只查找 warehouse 类型
                                        // 匹配名称和 sortingCode 的第二段
                                        const segments = w.sortingCode?.split('-') || [];
                                        const secondSegment = segments.length >= 2 ? segments[1] : w.sortingCode;
                                        return w.name === savedWarehouseName && secondSegment === savedWarehouseSortingCode;
                                    });
                                }
                                
                                return {
                                    warehouseId: matchedWarehouse?.id?.toString() || null,
                                    warehouseName: savedWarehouseName,
                                    sortingCode: matchedWarehouse?.sortingCode || undefined,
                                    line: contentConfig.dspLine || contentConfig.line || '',
                                    priceUsd: contentConfig.priceUsd ?? null
                                };
                            }
                        }
                    } catch (e) {
                        console.error(`[DSPCreateSchemeComp] 解析 contentPriceConfig 失败:`, e);
                    }
                    return {
                        warehouseId: null,
                        warehouseName: '',
                        sortingCode: undefined,
                        line: '',
                        priceUsd: null
                    };
                });

                newFeeItemDataMap.set(feeItemCodeNum, { cards });
                selectedFeeItemCodesSet.add(feeItemCodeNum);
            });

            setFeeItemDataMap(newFeeItemDataMap);
            setSelectedFeeItemCodes(selectedFeeItemCodesSet);
        }
    }, [schemeDetailData, viewMode, copyFromSchemeId, feeItems, warehouseData]);

    // 初始化费用项数据（创建模式）
    useEffect(() => {
        if (feeItems && feeItems.length > 0 && viewMode === 'create') {
            setFeeItemDataMap(prev => {
                const newMap = new Map(prev);
                let hasChanges = false;

                feeItems.forEach((feeItem: any) => {
                    const feeItemCode = feeItem.businessFeeCode;
                    const feeItemCodeNum = typeof feeItemCode === 'string' ? parseInt(feeItemCode, 10) : feeItemCode;
                    const existingData = newMap.get(feeItemCodeNum);

                    if (!existingData) {
                        // DSP 配送费初始化数据
                        newMap.set(feeItemCodeNum, {
                            cards: [{ warehouseId: null, warehouseName: '', sortingCode: undefined, line: '', priceUsd: null }]
                        });
                        hasChanges = true;
                    }
                });

                return hasChanges ? newMap : prev;
            });
        }
    }, [feeItems, viewMode]);

    // 根据费用项动态生成费用项列表
    const feeItemList = useMemo(() => {
        if (!feeItems || feeItems.length === 0) return [];

        return feeItems.map((feeItem: any) => {
            const feeItemCode = feeItem.businessFeeCode;
            const feeItemId = feeItem.id;
            const feeItemName = feeItem.businessFeeName;
            const feeItemCodeNum = typeof feeItemCode === 'string' ? parseInt(feeItemCode, 10) : feeItemCode;

            // 获取费用项数据
            const currentData = feeItemDataMap.get(feeItemCodeNum) || {
                cards: [{ warehouseId: null, warehouseName: '', sortingCode: undefined, line: '', priceUsd: null }]
            };

            // 更新费用项数据
            const updateFeeItemData = (newData: DspDeliveryFeeData) => {
                setFeeItemDataMap(prev => {
                    const newMap = new Map(prev);
                    newMap.set(feeItemCodeNum, newData);
                    return newMap;
                });
            };

            // DSP 场景使用 DspDeliveryFeeTabb 组件
            const children = (
                <DspDeliveryFeeTabb
                    tabId={feeItemId}
                    data={currentData}
                    onChange={updateFeeItemData}
                    disabled={isViewMode}
                />
            );

            const isSelected = selectedFeeItemCodes.has(feeItemCodeNum);

            const handleCheckboxChange = (e: any) => {
                const checked = e.target.checked;
                setSelectedFeeItemCodes(prev => {
                    const newSet = new Set(prev);
                    if (checked) {
                        newSet.add(feeItemCodeNum);
                    } else {
                        newSet.delete(feeItemCodeNum);
                    }
                    return newSet;
                });
            };

            return {
                label: (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Checkbox
                            checked={isSelected}
                            onChange={handleCheckboxChange}
                            onClick={(e) => e.stopPropagation()}
                            disabled={isViewMode}
                        />
                        {feeItemName}
                    </span>
                ),
                key: feeItemId || feeItemCode.toString(),
                feeItemCode: feeItemCode,
                children: children
            };
        });
    }, [feeItems, feeItemDataMap, selectedFeeItemCodes, isViewMode]);

    // 保存方案
    const handleSave = async () => {
        // 基础校验
        if (!pricingSchemeName || pricingSchemeName.trim() === '') {
            message.error('请输入方案名称');
            return;
        }
        if (!pricingSchemeDescription || pricingSchemeDescription.trim() === '') {
            message.error('请输入备注');
            return;
        }
        if (!businessScenario) {
            message.error('请选择业务场景');
            return;
        }

        // 检查是否有选中的费用项
        if (selectedFeeItemCodes.size === 0) {
            message.error('请至少选择一个费用项进行配置');
            return;
        }

        // 收集费用项配置 - DSP 使用固定的费用项代码
        const priceConfig: any[] = [];
        let hasValidationError = false;
        const validationErrors: string[] = [];

        // DSP 场景下，遍历所有选中的费用项
        feeItems.forEach((feeItem: any) => {
            const feeItemCode = feeItem.businessFeeCode;
            const feeItemName = feeItem.businessFeeName;
            const feeItemCodeNum = typeof feeItemCode === 'string' ? parseInt(feeItemCode, 10) : feeItemCode;

            if (!selectedFeeItemCodes.has(feeItemCodeNum)) {
                return;
            }

            const feeItemData = feeItemDataMap.get(feeItemCodeNum);

            if (!feeItemData || !feeItemData.cards || feeItemData.cards.length === 0) {
                validationErrors.push(`${feeItemName}：请配置费用项数据`);
                hasValidationError = true;
                return;
            }

            // 验证每个卡片
            for (let cardIndex = 0; cardIndex < feeItemData.cards.length; cardIndex++) {
                const card = feeItemData.cards[cardIndex];
                if (!card.warehouseId) {
                    validationErrors.push(`${feeItemName}（卡片${cardIndex + 1}）：请选择仓库`);
                    hasValidationError = true;
                }
                if (!card.line || card.line.trim() === '') {
                    validationErrors.push(`${feeItemName}（卡片${cardIndex + 1}）：请输入线路`);
                    hasValidationError = true;
                } else if (!isLineInputValid(card.line)) {
                    validationErrors.push(`${feeItemName}（卡片${cardIndex + 1}）：线路仅允许字母、数字、英文逗号、空格`);
                    hasValidationError = true;
                }
                if (card.priceUsd == null || card.priceUsd === undefined) {
                    validationErrors.push(`${feeItemName}（卡片${cardIndex + 1}）：请输入价格`);
                    hasValidationError = true;
                }
            }

            // 构建价格配置 - 使用 DSP 固定参数
            const allPriceConfig = feeItemData.cards
                .filter(card => card.warehouseId && card.line && card.priceUsd != null)
                .map((card, groupId) => {
                    // 从 sortingCode 中提取第二段作为 warehouseSortingCode
                    // 例如 "LAX-DC" -> "DC"
                    let warehouseSortingCode = '';
                    if (card.sortingCode) {
                        const segments = card.sortingCode.split('-');
                        warehouseSortingCode = segments.length >= 2 ? segments[1] : card.sortingCode;
                    }
                    
                    return {
                        groupId,
                        contentPriceConfig: JSON.stringify([{
                            warehouseSortingCode: warehouseSortingCode,
                            warehouseName: card.warehouseName || '',
                            dspLine: card.line?.trim() || '',
                            priceUsd: card.priceUsd
                        }])
                    };
                });

            if (allPriceConfig.length > 0) {
                priceConfig.push({
                    pricingFeeItemCode: DSP_PRICING_FEE_ITEM_CODE,
                    allPriceConfig
                });
            }
        });

        if (hasValidationError && validationErrors.length > 0) {
            const errorMessage = validationErrors.length === 1
                ? validationErrors[0]
                : `发现 ${validationErrors.length} 个错误：\n${validationErrors.map((err, idx) => `${idx + 1}. ${err}`).join('\n')}`;
            message.error(errorMessage, 5);
            return;
        }

        if (priceConfig.length === 0) {
            message.error('请至少配置一个费用项');
            return;
        }

        // DSP 场景下，使用固定的 clientId
        const payload: PricingSchemeInputData = {
            clientId: DSP_CLIENT_ID,
            pricingSchemeId: isEditMode ? pricingSchemeId : null,
            pricingSchemeName: pricingSchemeName.trim(),
            pricingSchemeDescription: pricingSchemeDescription.trim(),
            businessScenario,
            priceConfig
        };

        console.log('[DSPCreateSchemeComp] 提交数据:', JSON.stringify(payload, null, 2));

        const executeSave = async () => {
            try {
                if (isEditMode) {
                    const { data, errors } = await updatePricingScheme({
                        variables: { input: payload },
                        fetchPolicy: 'network-only'
                    });

                    if (errors && errors.length > 0) {
                        message.error(errors[0].message || '更新失败');
                        return;
                    }

                    const res = data?.updatePricingSchemeByClientId;
                    if (res?.success) {
                        message.success(t('settlement.priceCenter.messages.updateSuccess'));
                        if (onSaveSuccess) {
                            onSaveSuccess();
                        }
                    } else {
                        message.error(resolvePricingSchemeMessage(res, t('settlement.priceCenter.messages.updateFailed')));
                    }
                } else {
                    const { data, errors } = await submitNewPricingScheme({
                        variables: { input: payload },
                        fetchPolicy: 'network-only'
                    });

                    if (errors && errors.length > 0) {
                        message.error(errors[0].message || '保存失败');
                        return;
                    }

                    const res = data?.newPricingSchemeByClientId;
                    if (res?.success) {
                        message.success(t('settlement.priceCenter.messages.saveSuccess'));
                        if (onSaveSuccess) {
                            onSaveSuccess();
                        }
                    } else {
                        message.error(resolvePricingSchemeMessage(res, t('settlement.priceCenter.messages.saveFailed')));
                    }
                }
            } catch (e: any) {
                message.error(e?.message || t('errors.INTERNAL_ERROR'));
            }
        };

        if (isEditMode) {
            const bindCount = schemeDetailData?.getPricingSchemeDetail?.bindCount || 0;
            if (bindCount > 0) {
                Modal.confirm({
                    title: '确认更新',
                    content: '当前方案已被绑定，编辑后将影响绑定的方案，是否确认更新？',
                    okText: '确认',
                    cancelText: '取消',
                    onOk: executeSave
                });
                return;
            }
        }

        executeSave();
    };

    if (schemeDetailLoading) {
        return <div style={{ padding: '20px', textAlign: 'center' }}>加载中...</div>;
    }

    return (
        <div>
            <Row justify="start" gutter={[16, 8]}>
                <Col span={12}>
                    <InputFilterItem
                        label="方案名称"
                        value={pricingSchemeName}
                        onChange={(e) => setPricingSchemeName(e.target.value)}
                        spanClassName="w-[9em]"
                        disabled={isViewMode}
                        required={true}
                        placeholder="请输入方案名称"
                    />
                </Col>
            </Row>
            <Row justify="start" gutter={[16, 8]} style={{ marginTop: 16 }}>
                <Col span={24}>
                    <div className="flex flex-col">
                        <span className="text-gray-500 mb-1 text-left whitespace-nowrap text-sm" style={{ width: '9em' }}>
                            <span style={{ color: '#ff4d4f', marginRight: '4px' }}>*</span>
                            备注
                        </span>
                        <TextArea
                            value={pricingSchemeDescription}
                            onChange={(e) => setPricingSchemeDescription(e.target.value)}
                            disabled={isViewMode}
                            placeholder="请输入备注信息"
                            rows={3}
                            maxLength={100}
                            showCount
                        />
                    </div>
                </Col>
            </Row>

            {/* 费用项配置 */}
            <Row justify="center" gutter={[8, 16]} style={{ marginTop: 16 }}>
                <Col>
                    <div style={{ marginTop: '16px', marginBottom: '8px', color: '#666', fontSize: '12px' }}>
                        💡 只有✅该费用项才会被配置；
                        ⚠️ 金额只支持到两位小数
                    </div>
                    <PricingItemPanel
                        priceItemTabs={feeItemList}
                    />
                </Col>
            </Row>

            <Divider />
            <Row justify="end" style={{ margin: 25 }}>
                <Col>
                    <Button type="default" size="large" style={{ margin: 15 }} onClick={onCancel}>返回</Button>
                    {(viewMode === 'create' || viewMode === 'edit') && (
                        <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_COST_PRICE_PLAN_OPERATE]}>
                            <Button type="primary" size="large" style={{ margin: 15 }} onClick={handleSave} loading={saving}>保存</Button>
                        </HasResource>
                    )}
                </Col>
            </Row>
        </div>
    );
};

export default DSPCreateSchemeComp;
